import { ChatDetailsResponse } from '@choco-admin/types';
import { Button, Chip, Card, CardBody, Link as HeroLink } from '@heroui/react';
import React from 'react';
import {
  FiCopy,
  FiArrowLeft,
  FiMapPin,
  FiTruck,
  FiShoppingBag,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { useNotification } from '../../../../../contexts/NotificationContext';

interface EditChatHeaderProps {
  chatData: ChatDetailsResponse;
  chatId: string;
  onDuplicate: () => void;
}

export const EditChatHeader: React.FC<EditChatHeaderProps> = ({
  chatData,
  chatId,
  onDuplicate,
}) => {
  const { addNotification } = useNotification();

  const handleCopyChatId = async () => {
    try {
      await navigator.clipboard.writeText(chatId);
      addNotification({
        title: 'Chat ID Copied',
        message: 'Chat ID has been copied to clipboard.',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        title: 'Copy Failed',
        message: 'Failed to copy chat ID to clipboard.',
        type: 'error',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'primary';
    }
  };

  return (
    <div className="space-y-4">
      {/* Back Link */}
      <HeroLink
        as={Link}
        to="/chats"
        color="primary"
        className="inline-flex items-center gap-2"
      >
        <FiArrowLeft className="w-4 h-4" />
        Back to Chats
      </HeroLink>

      {/* Header Card */}
      <Card className="border border-default-200">
        <CardBody className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left: Chat Info */}
            <div className="flex-1 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h1 className="title-large">
                      {chatData.restaurantName || 'Chat'}
                    </h1>
                    <Chip
                      color={getStatusColor(chatData.status)}
                      variant="flat"
                      size="sm"
                      classNames={{
                        content: 'font-semibold',
                      }}
                    >
                      {chatData.status.charAt(0).toUpperCase() +
                        chatData.status.slice(1)}
                    </Chip>
                  </div>

                  {/* Key Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {chatData.supplierEntity && (
                      <div className="flex items-start gap-2">
                        <FiTruck className="w-4 h-4 mt-0.5 text-default-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-default-500 uppercase tracking-wide mb-0.5">
                            Distributor
                          </p>
                          <Link
                            to={`/suppliers/${chatData.supplierEntity.id}`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {chatData.supplierEntity.name}
                          </Link>
                        </div>
                      </div>
                    )}

                    {chatData.buyerEntity && (
                      <div className="flex items-start gap-2">
                        <FiShoppingBag className="w-4 h-4 mt-0.5 text-default-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-default-500 uppercase tracking-wide mb-0.5">
                            Buyer
                          </p>
                          <Link
                            to={`/buyers/${chatData.buyerEntity.id}`}
                            className="text-sm font-medium text-primary hover:underline"
                          >
                            {chatData.buyerEntity.name}
                          </Link>
                        </div>
                      </div>
                    )}

                    {chatData.address && (
                      <div className="flex items-start gap-2">
                        <FiMapPin className="w-4 h-4 mt-0.5 text-default-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-default-500 uppercase tracking-wide mb-0.5">
                            Delivery Address
                          </p>
                          <p className="text-sm text-foreground break-words">
                            {chatData.address}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-2">
                      <FiCopy className="w-4 h-4 mt-0.5 text-default-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-default-500 uppercase tracking-wide mb-0.5">
                          Chat ID
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-foreground bg-default-100 px-2 py-1 rounded">
                            {chatId}
                          </code>
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={handleCopyChatId}
                            aria-label="Copy Chat ID"
                            className="h-6 w-6 min-w-6"
                          >
                            <FiCopy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex-shrink-0">
              <Button
                variant="flat"
                color="primary"
                startContent={<FiCopy className="w-4 h-4" />}
                onPress={onDuplicate}
              >
                Duplicate Chat
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
